import { Spin } from 'antd';
import type * as React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { type IQueryFn, type IQueryOptionsFn } from './AsyncSelectInput.tsx';
import { type SelectOption } from './SelectInput.tsx';
import { useSelectedOptionLabel } from './select/useSelectedOptionLabel.tsx';

export interface IAsyncSelectChipValueConfig<
  TParams extends object,
  TItem extends object,
  TData,
> {
  /**
   * Stessa coppia `queryFn`/`queryOptions` del relativo `*SelectInput`:
   * `queryFn` serve a inferire `TData`, la fetch passa per `queryOptions`.
   */
  queryFn: IQueryFn<TParams, TData>;
  queryOptions: IQueryOptionsFn<TParams, TData>;
  initialParams?: TParams;
  /**
   * Mappa un record nell'opzione del chip. `value` DEVE essere non-null e
   * coincidere col valore salvato nel form (lo stesso che restituisce il
   * `*SelectInput`), altrimenti il chip non riesce a risolvere l'etichetta.
   */
  asOption: (item: TItem) => SelectOption;
  /**
   * Per i select a RICERCA (lista NON precaricata, es. utenti con ricerca
   * server-side ≥N caratteri): deriva i params per recuperare SOLO il record
   * selezionato a partire dal suo `value` (es. `value => ({ search: value })`).
   * Senza questo, `initialParams` (ricerca vuota) non troverebbe il record e il
   * chip cadrebbe sul fallback neutro. Per i select a lista completa, omettere.
   */
  searchByValue?: (value: string | string[]) => TParams;
}

export interface IAsyncSelectChipValueProps<
  TParams extends object,
  TItem extends object,
  TData,
> {
  value: string | string[];
  config: IAsyncSelectChipValueConfig<TParams, TItem, TData>;
}

/**
 * Risolve ID→etichetta nel chip della FilterBar riusando la stessa query del
 * relativo `*SelectInput` (stessa `queryKey` → cache condivisa). Fallback neutro
 * se l'ID non è risolvibile: mai l'ID grezzo.
 */
export function AsyncSelectChipValue<
  TParams extends object,
  TItem extends object,
  TData,
>(props: IAsyncSelectChipValueProps<TParams, TItem, TData>) {
  const { value, config } = props;
  const { queryOptions, initialParams, asOption, searchByValue } = config;
  const { t } = useTranslation();

  // Select a ricerca: la lista non è precaricata, quindi recuperiamo solo il
  // record selezionato derivando i params dal suo `value`. Altrimenti usiamo
  // `initialParams` (select a lista completa, già interamente in cache).
  const params = searchByValue ? searchByValue(value) : initialParams;

  const query = useQuery(
    queryOptions(params, {
      query: { keepPreviousData: true, staleTime: 5 * 60 * 1000 },
    }),
  );

  const options = useMemo(() => {
    const data = query.data as TData | undefined;
    // Supporta entrambi gli envelope di risposta usati nelle API: `dati`
    // (inquiries/users) e `data` (backoffice). `dati` ha priorità.
    const items = (
      Array.isArray(data)
        ? data
        : ((data as { dati?: TItem[] })?.dati ??
          (data as { data?: TItem[] })?.data ??
          [])
    ) as TItem[];
    return items.map(asOption);
  }, [query.data, asOption]);

  const label = useSelectedOptionLabel(
    value,
    options,
    Array.isArray(value) ? 'multiple' : undefined,
    'label',
  );

  if (query.isLoading) return <Spin size="small" />;

  if (!label) {
    const count = Array.isArray(value) ? value.length : 1;
    return (
      <>
        {count > 1
          ? t('filter-bar.selected-count', '{{count}} selezionati', { count })
          : t('filter-bar.value-selected', 'Valore selezionato')}
      </>
    );
  }

  return <>{label}</>;
}

/**
 * Costruisce il `renderValue` per un campo `Custom` async-select. Ritorna `null`
 * sui valori vuoti. Co-locare accanto al `*SelectInput`; vedi
 * `ui/.../filter-bar/README.md`.
 */
export function createAsyncSelectChipValue<
  TParams extends object,
  TItem extends object,
  TData,
>(
  config: IAsyncSelectChipValueConfig<TParams, TItem, TData>,
): (value: unknown) => React.ReactNode {
  return value => {
    if (
      value == null ||
      value === '' ||
      (Array.isArray(value) && value.length === 0)
    ) {
      return null;
    }
    return (
      <AsyncSelectChipValue
        value={value as string | string[]}
        config={config}
      />
    );
  };
}
